import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { useTestData } from "context/TestDataContext";
import { useTranslation } from "i18n/LanguageContext";
import { useState } from "react";
import { computeEvaluation } from "scoring/scoringEval";

export default function RtfUploadDialog({ open, handleClose, testName }) {
  const { updateTest } = useTestData();
  const { t, lang } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const handleFileUpload = async () => {
    setError(null);
    setLoading(true);

    try {
      // Use Electron API to open file dialog and parse RTF in main process
      const result = await window.electronAPI.openRTFFileDialog(lang);

      if (result.canceled) {
        setLoading(false);
        return;
      }

      if (!result.success) {
        throw new Error(result.error || "Failed to read and parse RTF file");
      }

      // Save to context using pre-mapped contextData from parser
      const { testType, contextData } = result.data;
      if (!contextData) {
        throw new Error(`No mapping config for test type: ${testType}`);
      }
      const evaluation = computeEvaluation(contextData.resultData, contextData.resultLoc);
      updateTest(testName, { ...contextData, evaluation });
      console.log("[RtfUploadDialog] Updated test in context:", testName);
      setLoading(false);
      handleClose();
    } catch (err) {
      console.error("RTF parsing error:", err);
      setError(err.message || "Failed to parse RTF file");
      setLoading(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setError(null);
    setLoading(true);

    try {
      const files = Array.from(e.dataTransfer.files);

      if (files.length === 0) {
        setLoading(false);
        return;
      }

      const file = files[0];

      // Validate file extension
      if (!file.name.toLowerCase().endsWith(".rtf")) {
        throw new Error("Please drop a valid RTF file (.rtf extension)");
      }

      // Pass the File object — Electron preload extracts .path, web shim uploads.
      const result = await window.electronAPI.parseDroppedRTFFile(file, lang);

      if (!result.success) {
        throw new Error(result.error || "Failed to parse RTF file");
      }

      // Save to context using pre-mapped contextData from parser
      const { testType, contextData } = result.data;
      if (contextData) {
        const evaluation = computeEvaluation(contextData.resultData, contextData.resultLoc);
        updateTest(testName, { ...contextData, evaluation });
        console.log("[RtfUploadDialog] Updated test in context:", testName);
        handleClose();
      } else {
        console.warn("[RtfUploadDialog] No contextData mapping for", testType);
      }
      setLoading(false);
    } catch (err) {
      console.error("RTF drop error:", err);
      setError(err.message || "Failed to process dropped file");
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDialogClose = () => {
    // Reset state when closing
    setJsonData(null);
    setFileName("");
    setError(null);
    setLoading(false);
    handleClose();
  };

  const handleClearPreview = () => {
    setJsonData(null);
    setFileName("");
    setError(null);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="rtf-dialog-title"
    >
      <AppBar position="relative" color="transparent" elevation={0}>
        <Toolbar>
          <Typography
            variant="h6"
            color="secondary"
            sx={{ flexGrow: 1 }}
            id="rtf-dialog-title"
          >
            {t("rtf.title")}
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleDialogClose}
            aria-label="close"
          >
            <HighlightOffRoundedIcon color="secondary" />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box mx={3} my={3}>
        <Stack spacing={3}>
          {/* Upload Section */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={<UploadFileIcon />}
              onClick={handleFileUpload}
              disabled={loading}
            >
              {loading ? t("rtf.parsing") : t("rtf.openFile")}
            </Button>

            {fileName && (
              <Box>
                <Typography variant="body2" color="textSecondary">
                  Selected: <strong>{fileName}</strong>
                </Typography>
              </Box>
            )}

            {jsonData && (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearPreview}
                  disabled={loading}
                >
                  Clear
                </Button>
                <Button variant="outlined" color="primary" disabled={loading}>
                  calculate
                </Button>
              </>
            )}
          </Stack>

          {/* Loading State */}
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Empty State with Drag and Drop */}
          {!jsonData && !loading && !error && (
            <Box
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: "2px dashed",
                borderColor: isDragging ? "primary.main" : "divider",
                borderRadius: 2,
                backgroundColor: isDragging ? "action.hover" : "transparent",
                transition: "all 0.2s ease-in-out",
                cursor: "pointer",
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={6}
              >
                <UploadFileIcon
                  sx={{
                    fontSize: 60,
                    color: isDragging ? "primary.main" : "text.secondary",
                    mb: 2,
                    transition: "color 0.2s ease-in-out",
                  }}
                />
                <Typography
                  variant="body1"
                  color={isDragging ? "primary" : "textSecondary"}
                  sx={{ transition: "color 0.2s ease-in-out" }}
                >
                  {isDragging ? t("rtf.dragDrop") : `${t("rtf.dragDrop")} — ${t("rtf.orBrowse")}`}
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
}
